<template>
  <div
    :class="[
      $style.formContainer,
      { [$style.verifyMfaFormContainer]: hasSelectedFactor },
    ]"
  >
    <ChooseMfaError
      v-if="hasNoAvailableMfaFactors"
      v-bind="$props"
      header="Verify Your Identity"
      :error="errorMessage"
      :subheader="subheaderText"
      :buttonProps="{
        action: backToLogin,
        text: 'Return To Login'
      }"
    />

    <LoginCard
      v-if="availableFactors.length >= 1"
      :header="headerText"
      :className="selectedFactor.name"
      :showCustomLogo="!hasSelectedFactor"
    >
      <h2
        :class="[
          $style.subheader,
          { [$style.verifyMfaSubheader]: hasSelectedFactor }
        ]"
      >
        {{ subheaderText }}
      </h2>

      <div :class="$style.mfaButtons">
        <button
          v-for="factor in availableFactors"
          :key="factor.name"
          :class="$style.mfaButton"
          @click="() => request(factor.name, { shouldPromptImmediately: true })"
          :disabled="factor.name === $options.webauthn && !isWebAuthnSupported"
          type="button"
        >
          <div :class="$style.factorIconWrapper">
            <img
              :class="[factorClassNames[factor.name]]"
              :src="factor.iconUrl"
              :data-test-id="`${factor.name}-svg`"
            >
          </div>
          {{ factor.description }}
        </button>
      </div>
      <span
        v-if="!isWebAuthnSupported"
        :class="$style.webauthnUnsupported"
      >
        Security Keys are not supported on this browser.
      </span>
    </LoginCard>
    <LogoIcon />
  </div>
</template>

<script>
// Libraries
import { get } from 'lodash';

// Constants
import { LocalStorageConsts, LoginConsts } from 'client/util/Constants';
import MfaConstants from 'client/containers/Login/MfaConstants';

// Components
import ChooseMfaError from 'client/containers/Login/ChooseMfaError.vue';
import LoginCard from 'client/components/LoginCard.vue';

// Utils
import { webAuthnSupported } from 'client/util/FeatureSupport';
import LocalStorageService from 'client/util/LocalStorageService';
import loginUtil from 'client/components/mixins/loginUtil';

const { previousMfaMethod } = LocalStorageConsts;
const { responseMessages } = LoginConsts;
/**
 * The ChooseMfa component allows a system user to select the method they would
 * like to use for multi-factor authentication. Currently the supported methods
 * are Duo, Push, Totp, and WebAuthN. It is intended to *only* direct a user to the
 * user's preferred MFA authentication, *not* to trigger that authentication.
 */
export default {
  name: 'ChooseMfa',

  components: {
    ChooseMfaError,
    LoginCard,
  },

  mixins: [
    loginUtil,
  ],

  props: {
    context: {
      type: String,
      default: '',
    },
    factors: {
      type: Array,
      default: () => ([]),
    },
    redirectTo: {
      type: String,
      default: null,
    },
    selectedFactor: {
      type: Object,
      default: () => ({}),
    },
    state: {
      type: String,
      default: undefined,
    },
  },

  webauthn: MfaConstants.mfaNames.webauthn,

  beforeMount() {
    let lastFactor = this.factors.find(
      factor => factor.name === LocalStorageService.getItem(previousMfaMethod),
    );

    // WebAuthn edge cases
    // If webauthn is last factor and is no longer supported there are three paths:
    // 1) if it's the only factor redirect anyway
    // 2) if one other factor is available then redirect to it
    // 3) if multiple other factors are available then stay on /choosemfa
    if (
      lastFactor
      && lastFactor.name === this.$options.webauthn
      && !this.isWebAuthnSupported
    ) {
      const remainingFactors = this.factors.filter(
        factor => factor.name !== this.$options.webauthn,
      );

      lastFactor = remainingFactors.length === 1
        ? remainingFactors[0]
        : undefined;
    }

    if (lastFactor === undefined) {
      LocalStorageService.removeItem(previousMfaMethod);
    }

    const factor = this.factors.length === 1
      ? this.factors[0]
      : lastFactor;

    if (factor && this.$route.name !== factor.route.name) {
      this.request(factor.name);
    }
  },

  mounted() {
    if (this.hasNoAvailableMfaFactors) {
      const message = this.context === LoginConsts.passwordReset
        ? responseMessages.mfaUnavailableForPwdReset
        : responseMessages.mfaUnavailable;

      this.setErrorMessage(message);
    }
  },

  computed: {
    availableFactors() {
      const sortOrder = {
        push: 0,
        totp: 1,
        webauthn: 2,
        duo: 3,
      };

      // There isn't actually a side-effect. It is simply using a const to make
      // the sort more readable.
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties, vue/no-mutating-props
      return this.factors
        .sort((a, b) => sortOrder[a.name] - sortOrder[b.name])
        .filter(factor => factor.name !== this.selectedFactor.name);
    },

    hasSelectedFactor() {
      return !!this.selectedFactor.name;
    },

    headerText() {
      return this.hasSelectedFactor
        ? ''
        : 'Verify Your Identity';
    },

    hasNoAvailableMfaFactors() {
      return this.factors.length < 1;
    },

    isWebAuthnSupported() {
      return webAuthnSupported();
    },

    passwordResetInfo() {
      const [factor] = this.availableFactors;
      return get(factor, 'route.params.passwordResetInfo', {});
    },

    subheaderText() {
      return this.hasSelectedFactor
        ? 'Or choose a different way:'
        : 'This extra step shows you\'re really you. Choose a method to verify yourself:';
    },
  },

  methods: {
    backToLogin() {
      this.$router.replace({ name: 'Login' });
    },

    request(factorName, options = {}) {
      const factor = this.factors.find(f => factorName === f.name);

      LocalStorageService.setItem(previousMfaMethod, factor.name);

      // TODO: switch to `push` once we get the factors moved to state instead of params.
      this.$router.replace(
        {
          name: `${factor.route.name}`,
          params: {
            ...factor.route.params,
            factor,
            factors: this.factors,
            shouldPromptImmediately: !!options.shouldPromptImmediately,
          },
        },
      );
    },
  },
};
</script>

<style module>
@import '../../css/login-components.css';

@value (
  jcWhite100,
  jcGrey100,
  jcGrey200,
  jcGrey300,
  jcGrey800,
  jcGrey900,
  jcError100
) from 'css/brand-colors.css';

.verifyMfaFormContainer {
  padding: 0;
}

.subheader {
  color: jcGrey800;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 1.5rem auto;
  max-width: 40ch;
}

.verifyMfaSubheader {
  margin: 0;
  text-align: left;
}

.error {
  background-color: jcError100;
  border-radius: 3px;
  color: jcGrey900;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0;
  padding: 1rem;
  text-align: left;
}

.mfaButtons {
  display: flex;
  flex-direction: column;
}

.mfaButton {
  align-items: center;
  background-color: jcWhite100;
  border: 1px solid jcGrey900;
  border-radius: 4px;
  color: jcGrey900;
  cursor: pointer;
  display: flex;
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0.5rem 0;
  min-height: 2.625rem;
  transition: background-color 0.2s ease-in-out;
  width: 100%;
}

.mfaButton:disabled {
  border-color: jcGrey300;
  color: jcGrey300;
  cursor: no-drop;
  order: 3;
}

.mfaButton:hover:not([disabled]) {
  background-color: jcGrey200;
}

.webauthnUnsupported {
  color: jcGrey800;
  font-size: 0.75rem;
  font-weight: 400;
}

.iconWrapper {
  align-items: center;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
}

.factorIconWrapper {
  composes: iconWrapper;
  background-color: jcGrey100;
  border-radius: 50%;
  height: 2rem;
  margin-left: 0.5rem;
  margin-right: 0.75rem;
  vertical-align: top;
  width: 2rem;
}
</style>

<style module="factorClassNames">
@value (duoIconBackground) from 'css/MfaColors.css';
@value (jcGrey100) from 'css/brand-colors.css';

.duo,
.push,
.totp,
.webauthn {
  height: 1.25rem;
  width: 1.25rem;
}

.duo {
  background-color: duoIconBackground;
}

img.duo {
  background-color: jcGrey100;
}
</style>
